<template>
  <div class="projects-panel">
    <div v-if="projectMode === 'list'">
      <SkeletonList v-if="showSkeleton" variant="card" :count="3" />
      <div v-else-if="projects.length === 0" class="item-meta">Nenhum projeto disponivel.</div>
      <div v-else class="project-grid">
        <article
          v-for="proj in projects"
          :key="proj.id"
          class="project-card clickable-card"
          role="button"
          tabindex="0"
          @click="$emit('extend-project', proj.id)"
          @keydown.enter.prevent="$emit('extend-project', proj.id)"
          @keydown.space.prevent="$emit('extend-project', proj.id)"
        >
          <div class="project-main">
            <div class="project-left">
              <h3 class="project-title">{{ proj.name }}</h3>
              <div class="item-meta">Responsavel: {{ leadResponsible(proj) }}</div>
              <div class="item-meta">
                Status:
                <span :class="insightFor(proj.id).status === 'atrasado' ? 'status-late' : 'status-ok'">
                  {{ insightFor(proj.id).status === 'atrasado' ? 'Atrasado' : 'Em dia' }}
                </span>
              </div>
              <div class="item-meta">Previsao de conclusao: {{ forecastText(proj.id) }}</div>
              <div class="gauge-wrap">
                <div class="gauge-shell">
                  <svg viewBox="0 0 200 120" class="gauge-svg" aria-hidden="true">
                    <path class="gauge-track" d="M20 100 A80 80 0 0 1 180 100" pathLength="100" />
                    <path class="gauge-planned" d="M20 100 A80 80 0 0 1 180 100" pathLength="100" :style="arcStyle(insightFor(proj.id).plannedPercent)" />
                    <path class="gauge-actual" d="M20 100 A80 80 0 0 1 180 100" pathLength="100" :style="arcStyle(insightFor(proj.id).actualPercent)" />
                  </svg>
                  <div class="gauge-value">{{ percentText(insightFor(proj.id).actualPercent) }}</div>
                </div>
                <div class="gauge-legend">
                  <span><i class="dot dot-planned"></i>Planejado {{ percentText(insightFor(proj.id).plannedPercent) }}</span>
                  <span><i class="dot dot-actual"></i>Real {{ percentText(insightFor(proj.id).actualPercent) }}</span>
                </div>
              </div>
            </div>
            <div class="project-right">
              <img v-if="hasPhoto(proj.photoDataUrl)" class="project-photo" :src="photoSrc(proj.photoDataUrl)" :alt="proj.name" />
              <div v-else class="project-photo project-photo-empty">Sem foto</div>
            </div>
          </div>
        </article>
      </div>
    </div>

    <div v-else-if="projectMode === 'view'" class="item-card">
      <h3 class="item-title">{{ selectedProject?.name }}</h3>
      <div class="item-meta">Acesso: {{ selectedProject?.accessMode === 'read' ? 'Somente leitura' : 'Edicao' }}</div>
      <div class="item-meta">Responsaveis: {{ (selectedProject?.responsibleNames || []).join(', ') || '-' }}</div>
      <div class="item-meta">Temporarios: {{ selectedProject ? tempResponsibleNames(selectedProject) : '-' }}</div>
      <div class="item-meta">Criado em: {{ selectedProject ? formatReportDate(selectedProject.createdAt.slice(0, 10)) : '-' }}</div>
      <img v-if="hasPhoto(selectedProject?.photoDataUrl || '')" class="project-photo-large" :src="photoSrc(selectedProject?.photoDataUrl || '')" :alt="selectedProject?.name || 'Projeto'" />
      <div class="actions">
        <button class="btn btn-ghost" type="button" @click="$emit('close-project-mode')">Fechar</button>
      </div>
    </div>

    <div v-else class="form-grid">
      <div v-if="selectedProject" class="item-card">
        <h3 class="item-title">{{ selectedProject.name }}</h3>
        <div class="item-meta">Acesso: {{ selectedProject.accessMode === 'read' ? 'Somente leitura' : 'Edicao' }}</div>
        <div class="item-meta">Responsaveis: {{ (selectedProject.responsibleNames || []).join(', ') || '-' }}</div>
        <div class="item-meta">Temporarios: {{ tempResponsibleNames(selectedProject) }}</div>
        <div class="item-meta">Criado em: {{ formatReportDate(selectedProject.createdAt.slice(0, 10)) }}</div>
        <img v-if="hasPhoto(selectedProject.photoDataUrl)" class="project-photo-large" :src="photoSrc(selectedProject.photoDataUrl)" :alt="selectedProject.name" />
      </div>

      <div class="input-field">
        <label for="project-name">Nome do projeto</label>
        <input id="project-name" :value="projectForm.name" type="text" placeholder="Nome do projeto" @input="$emit('update-project-form', { field: 'name', value: ($event.target as HTMLInputElement).value })" />
      </div>

      <div class="input-field">
        <label for="project-photo">Foto do projeto (unica)</label>
        <input id="project-photo" type="file" accept="image/*" @change="onProjectPhotoSelected" />
        <img v-if="projectForm.photoDataUrl" class="project-photo-large" :src="projectForm.photoDataUrl" alt="preview projeto" />
      </div>

      <div class="input-field">
        <label>Responsaveis</label>
        <div v-if="isAdmin" class="grid" style="gap: 10px;">
          <div v-for="(username, index) in projectForm.responsibleUsernames" :key="`main-resp-${index}`" class="list-row">
            <select :value="username" @change="$emit('update-project-form', { field: 'responsibleUsernames', index, value: ($event.target as HTMLSelectElement).value })">
              <option value="">Selecione</option>
              <option v-for="acc in accounts" :key="acc.username" :value="acc.username">
                {{ acc.name }} ({{ acc.username }})
              </option>
            </select>
            <button class="btn btn-accent" type="button" :disabled="projectForm.responsibleUsernames.length <= 1" @click="$emit('remove-responsible', index)">Excluir</button>
          </div>
          <button class="btn btn-ghost" type="button" @click="$emit('add-responsible')">+</button>
        </div>
        <div v-else class="item-meta">
          {{ (selectedProject?.responsibleNames || []).join(', ') || '-' }}
        </div>
      </div>

      <div class="input-field">
        <label>Acrescentar responsaveis temporarios</label>
        <div class="grid" style="gap: 10px;">
          <div v-for="(temp, index) in projectForm.tempResponsibles" :key="`temp-${index}`" class="temp-row">
            <select :value="temp.username" @change="emitTemp(index, 'username', ($event.target as HTMLSelectElement).value)">
              <option value="">Selecione</option>
              <option v-for="acc in subordinateOptions" :key="acc.username" :value="acc.username">
                {{ acc.name }} ({{ acc.username }})
              </option>
            </select>
            <template v-if="!temp.useSpecificDate">
              <input type="number" min="1" :value="temp.days" placeholder="Dias" @input="emitTemp(index, 'days', ($event.target as HTMLInputElement).value)" />
            </template>
            <template v-else>
              <input type="date" :value="temp.startDate" @input="emitTemp(index, 'startDate', ($event.target as HTMLInputElement).value)" />
              <input type="date" :value="temp.endDate" @input="emitTemp(index, 'endDate', ($event.target as HTMLInputElement).value)" />
            </template>
            <button class="link link-inline" type="button" @click="emitTemp(index, 'useSpecificDate', !temp.useSpecificDate)">
              {{ temp.useSpecificDate ? 'Quantidade de dias' : 'Data especifica' }}
            </button>
            <button class="btn btn-accent" type="button" @click="$emit('remove-temp-responsible', index)">Excluir</button>
          </div>
          <button class="btn btn-ghost" type="button" @click="$emit('add-temp-responsible')">+</button>
        </div>
      </div>

      <div class="actions">
        <button class="btn btn-primary" type="button" @click="$emit('save-project')">Salvar</button>
        <button class="btn btn-ghost" type="button" @click="$emit('close-project-mode')">Cancelar</button>
      </div>
      <p v-if="projectError" class="subtitle error-text">{{ projectError }}</p>
    </div>
  </div>
</template>

<script lang="ts">
import { computed, defineComponent, type PropType } from 'vue'
import { projectsStore, type Project } from '@/store/projects'
import type { Account } from '@/store/accounts'
import SkeletonList from '@/components/skeleton/SkeletonList.vue'

interface TempResponsibleFormItem {
  username: string
  days: string
  startDate: string
  endDate: string
  useSpecificDate: boolean
}

interface ProjectFormModel {
  id: string
  name: string
  photoDataUrl: string
  responsibleUsernames: string[]
  tempResponsibles: TempResponsibleFormItem[]
}

export default defineComponent({
  name: 'MyProjectsPanel',
  components: { SkeletonList },
  props: {
    projects: { type: Array as PropType<Project[]>, default: () => [] },
    projectInsights: { type: Object as PropType<Record<string, { status: 'em-dia' | 'atrasado'; forecastDate: string; plannedPercent: number; actualPercent: number }>>, default: () => ({}) },
    accounts: { type: Array as PropType<Account[]>, default: () => [] },
    projectMode: { type: String, required: true },
    projectForm: { type: Object as PropType<ProjectFormModel>, required: true },
    projectError: { type: String, default: '' },
    selectedProject: { type: Object as PropType<Project | null>, default: null },
    isAdmin: { type: Boolean, default: false },
    currentUsername: { type: String, default: '' },
    subordinateOptions: { type: Array as PropType<Array<{ username: string; name: string }>>, default: () => [] },
    formatReportDate: { type: Function as PropType<(value: string) => string>, required: true }
  },
  emits: [
    'view-project',
    'extend-project',
    'edit-project',
    'close-project-mode',
    'save-project',
    'update-project-form',
    'add-responsible',
    'remove-responsible',
    'add-temp-responsible',
    'remove-temp-responsible',
    'update-temp-responsible'
  ],
  setup(props, { emit }) {
    const showSkeleton = computed(() => projectsStore.state.loading && props.projects.length === 0)
    const emitTemp = (index: number, field: string, value: string | boolean) => {
      emit('update-temp-responsible', { index, field, value })
    }
    const tempResponsibleNames = (proj: Project) => {
      const list = (proj.tempResponsibles || [])
        .map((temp) => {
          const account = props.accounts.find((item) => item.username === temp.username)
          return account?.name || temp.username
        })
        .filter((value) => Boolean(value))
      return list.length ? list.join(', ') : 'Nenhum'
    }
    const leadResponsible = (proj: Project) => {
      if (proj.responsibleNames && proj.responsibleNames.length > 0) return proj.responsibleNames[0]
      if (proj.ownerName) return proj.ownerName
      return '-'
    }
    const insightFor = (projectId: string) => props.projectInsights[projectId] || { status: 'em-dia', forecastDate: '', plannedPercent: 0, actualPercent: 0 }
    const forecastText = (projectId: string) => {
      const info = insightFor(projectId)
      if (!info.forecastDate) return '-'
      return props.formatReportDate(info.forecastDate)
    }
    const clampPercent = (value: number) => Math.max(0, Math.min(100, Number(value || 0)))
    const percentText = (value: number) => `${Math.round(clampPercent(value))}%`
    const arcStyle = (value: number) => ({
      strokeDasharray: `${clampPercent(value)} 100`
    })
    const onProjectPhotoSelected = (event: Event) => {
      const input = event.target as HTMLInputElement
      const file = input.files?.[0]
      if (!file) return
      const reader = new FileReader()
      reader.onload = () => {
        if (typeof reader.result === 'string') {
          emit('update-project-form', { field: 'photoDataUrl', value: reader.result })
        }
      }
      reader.readAsDataURL(file)
      input.value = ''
    }
    const hasPhoto = (value: string) => Boolean(`${value || ''}`.trim())
    const photoSrc = (value: string) => {
      const raw = `${value || ''}`.trim()
      if (!raw) return ''
      if (raw.startsWith('/uploads/')) {
        const apiBase = process.env.VUE_APP_API_BASE || 'http://localhost:3001'
        return `${apiBase}${raw}`
      }
      if (raw.startsWith('data:') || raw.startsWith('http://') || raw.startsWith('https://') || raw.startsWith('/')) {
        return raw
      }
      // Compatibilidade com fotos legadas salvas como base64 puro.
      return `data:image/jpeg;base64,${raw}`
    }
    return {
      showSkeleton,
      emitTemp,
      tempResponsibleNames,
      leadResponsible,
      insightFor,
      forecastText,
      percentText,
      arcStyle,
      onProjectPhotoSelected,
      hasPhoto,
      photoSrc
    }
  }
})
</script>

<style scoped lang="scss">
.project-grid {
  display: grid;
  grid-template-columns: 1fr;
  gap: 14px;
}

.project-card {
  border: 1px solid rgba(31, 30, 26, 0.12);
  border-radius: 12px;
  overflow: hidden;
  background: rgba(255, 255, 255, 0.9);
}

.clickable-card {
  cursor: pointer;
  transition: transform .18s ease, box-shadow .18s ease, border-color .18s ease;
}

.clickable-card:hover,
.clickable-card:focus-visible {
  transform: translateY(-1px);
  box-shadow: 0 10px 22px rgba(31, 30, 26, 0.1);
  border-color: rgba(79, 51, 0, 0.35);
  outline: none;
}

.project-main {
  width: 100%;
  aspect-ratio: 4 / 3;
  display: grid;
  grid-template-columns: 3fr 2fr;
}

.project-left {
  padding: 14px;
  display: grid;
  align-content: start;
  gap: 8px;
  position: relative;
}

.project-title {
  margin: 0;
  font-size: 28px;
  line-height: 1.08;
}

.gauge-wrap { margin-top: 8px; }
.gauge-shell { width: 220px; position: relative; }
.gauge-svg { width: 220px; height: 120px; display: block; }
.gauge-track,
.gauge-planned,
.gauge-actual {
  fill: none;
  stroke-linecap: round;
  stroke-width: 12;
  stroke-dasharray: 0 100;
}
.gauge-track { stroke: rgba(31, 30, 26, 0.16); stroke-dasharray: 100 100; }
.gauge-planned { stroke: #e1a12f; opacity: .95; }
.gauge-actual { stroke: var(--system-navbar, #4f3300); }
.gauge-value {
  position: absolute;
  left: 50%;
  bottom: 2px;
  transform: translateX(-50%);
  font-size: 20px;
  font-weight: 800;
  color: #1f1e1a;
}
.gauge-legend { display:grid; gap:4px; font-size:12px; color:#4b463f; margin-top: 2px; }
.dot { width:10px; height:10px; border-radius:50%; display:inline-block; margin-right:6px; vertical-align:middle; }
.dot-track { background: rgba(31, 30, 26, 0.22); }
.dot-planned { background: #e1a12f; }
.dot-actual { background: var(--system-navbar, #4f3300); }

.project-right {
  min-width: 0;
}

.project-photo {
  width: 100%;
  height: 100%;
  object-fit: cover;
}

.project-photo-empty {
  display: grid;
  place-items: center;
  background: #ececec;
  color: #646464;
  font-size: 13px;
}

.project-photo-large {
  width: min(280px, 100%);
  max-height: 180px;
  object-fit: cover;
  border-radius: 10px;
  margin-top: 10px;
}

.status-ok {
  color: #1f8a45;
  font-weight: 700;
}

.status-late {
  color: #bf2b2b;
  font-weight: 700;
}

.temp-row { display: grid; grid-template-columns: minmax(180px,1fr) minmax(120px,.5fr) minmax(130px,.5fr) minmax(130px,.5fr) auto auto; gap: 8px; align-items: center; }
.link { background: transparent; border: none; cursor: pointer; color: #2c2b27; }
.link:hover { color: #2f6fce; }
.link-inline { padding: 6px 10px; }
.error-text { color: #a54b2a; }

@media (min-aspect-ratio: 4/3) and (min-width: 1100px) {
  .project-grid {
    grid-template-columns: repeat(2, minmax(0, 1fr));
  }
}

@media (max-width: 900px) {
  .project-main {
    grid-template-columns: 1fr;
    aspect-ratio: auto;
  }

  .project-right {
    max-height: 220px;
  }

  .project-left {
    padding-bottom: 64px;
  }
}
</style>
